/**
 * PurchasingPage — `/production/purchasing` (round 19; order to invoice doc
 * 57 §3; supplier interface spec 09 §2): the operational side of purchase
 * orders, under Production beside Orders. Finance › Purchase orders keeps the
 * money.
 *
 * Five tabs, each from `GET /views/purchasing` (filter, search and paging on
 * the server; every tab's count in the one response):
 * - To place: purchase orders asked to be placed, to place by hand, oldest
 *   first, after any line a supplier rejected (marked "Rejected by
 *   supplier", with *Change supplier…* on the order's Release step).
 *   *Place one at a time →* walks the queue on the placement page.
 * - Awaiting acknowledgement: sent to the supplier (through the portal), not
 *   yet answered; late first (mock-up 9). *Chase* when late or not opened.
 * - Despatch due: placed, waiting on the supplier to despatch; late first.
 * - Arriving: despatched, on its way; late first.
 * - All: every purchase order, newest first.
 *
 * A row opens its purchase order where it waits (`step`). Staff with
 * `purchase_orders:view`, behind the `orders` feature; the writes are offered
 * only where the row carries their URL (`orders:manage`).
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  PurchaseOrderStep,
  PurchasingItem,
  PurchasingList,
  PurchasingRow,
  PurchasingTab,
  RejectedLineRow,
} from "@shared-core/data/order-to-invoice-types";
import { orderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { paths, type PurchaseOrderTab } from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Skeleton } from "../app/components/primitives/skeleton";
import { cn } from "../app/components/primitives/utils";
import { ListPagination } from "../app/components/patterns/ListPagination";

const short = formatShortDateOrDash;

const TABS: Array<{ tab: PurchasingTab; label: string }> = [
  { tab: "to_place", label: "To place" },
  { tab: "awaiting_acknowledgement", label: "Awaiting acknowledgement" },
  { tab: "despatch_due", label: "Despatch due" },
  { tab: "arriving", label: "Arriving" },
  { tab: "all", label: "All" },
];

const EMPTY: Record<PurchasingTab, string> = {
  to_place:
    "Nothing to place by hand. Released lines to suppliers with an API place themselves.",
  awaiting_acknowledgement:
    "Nothing is waiting on a supplier to acknowledge it.",
  despatch_due: "Nothing placed is waiting to despatch.",
  arriving: "Nothing is on its way.",
  all: "No purchase orders yet.",
};

/** The purchase order page's tab for the step a row waits on (its page has tabs, not round 19's chain). */
const STEP_TAB: Record<PurchaseOrderStep, PurchaseOrderTab> = {
  raised: "lines",
  placed: "now",
  despatched: "now",
  arrived: "receipts",
  billed: "purchase-invoices",
};

/** Mock-up 9's *Waiting on* pill, by the tone the server gave it. */
const WAITING_TONE = {
  bad: "error",
  warn: "warning",
  muted: "neutral",
} as const;

function isTab(value: string | null): value is PurchasingTab {
  return TABS.some((t) => t.tab === value);
}

function Dot({ row }: { row: PurchasingRow }) {
  return (
    <span
      className={cn(
        "inline-block size-2 rounded-full",
        row.dot === "late" && "bg-red-600",
        row.dot === "at_risk" && "bg-amber-500",
      )}
      {...(row.dot
        ? {
            role: "img",
            "aria-label": row.dot_reason ?? row.dot,
            title: row.dot_reason ?? undefined,
          }
        : { "aria-hidden": true })}
    />
  );
}

/** What on a To place row: the lines, and why it was not placed automatically when it was tried. */
function ToPlaceWhat({ row }: { row: PurchasingRow }) {
  return (
    <>
      {row.what}
      {(row.placement_failed_reason || row.placement_owed) && (
        <span className="block text-xs text-amber-700 dark:text-amber-400">
          {row.placement_failed_reason
            ? `Not placed automatically: ${row.placement_failed_reason}`
            : "Placing automatically: retrying"}
        </span>
      )}
    </>
  );
}

/** A date with how sure it is underneath ("Assumed from the table"): the backend's words (N5). */
function DateWithNote({ row, on }: { row: PurchasingRow; on: string | null }) {
  return (
    <>
      {short(on)}
      {row.expected_note && (
        <span className="block text-xs text-muted-foreground">
          {row.expected_note}
        </span>
      )}
    </>
  );
}

type Column = {
  heading: string;
  cell: (row: PurchasingRow) => ReactNode;
  isDate?: boolean;
};

/** The columns each purchase order tab shows, after the dot and the PO: what a person needs to act on it. */
const COLUMNS: Record<PurchasingTab, Column[]> = {
  to_place: [
    { heading: "Supplier", cell: (r) => r.supplier_name },
    { heading: "What", cell: (r) => <ToPlaceWhat row={r} /> },
    { heading: "Order line", cell: (r) => r.order_line },
    {
      heading: "Released",
      cell: (r) =>
        r.place_by_label ? (
          <span className="text-muted-foreground">{r.place_by_label}</span>
        ) : (
          short(r.released_on)
        ),
      isDate: true,
    },
    { heading: "Ready by", cell: (r) => short(r.ready_by), isDate: true },
    { heading: "Needed by", cell: (r) => short(r.needed_by), isDate: true },
  ],
  awaiting_acknowledgement: [
    { heading: "Supplier", cell: (r) => r.supplier_name },
    {
      heading: "Sent by",
      cell: (r) =>
        r.sent_by_label ? (
          <Badge variant="outline">{r.sent_by_label}</Badge>
        ) : (
          "—"
        ),
    },
    { heading: "Sent", cell: (r) => short(r.sent_on), isDate: true },
    {
      heading: "Waiting on",
      cell: (r) =>
        r.waiting_on ? (
          <Badge variant={WAITING_TONE[r.waiting_on_tone ?? "muted"]}>
            {r.waiting_on}
          </Badge>
        ) : (
          "—"
        ),
    },
    { heading: "Ready by", cell: (r) => short(r.ready_by), isDate: true },
  ],
  despatch_due: [
    { heading: "Supplier", cell: (r) => r.supplier_name },
    { heading: "What", cell: (r) => r.what },
    { heading: "Order line", cell: (r) => r.order_line },
    { heading: "Placed", cell: (r) => short(r.placed_on), isDate: true },
    {
      heading: "Despatch due",
      cell: (r) => <DateWithNote row={r} on={r.despatch_due_on} />,
      isDate: true,
    },
  ],
  arriving: [
    { heading: "Supplier", cell: (r) => r.supplier_name },
    { heading: "What", cell: (r) => r.what },
    { heading: "Order line", cell: (r) => r.order_line },
    {
      heading: "Despatched",
      cell: (r) =>
        `${short(r.despatched_on)}${r.carrier ? ` · ${r.carrier}` : ""}`,
    },
    {
      heading: "Arriving",
      cell: (r) => <DateWithNote row={r} on={r.arriving_on} />,
      isDate: true,
    },
  ],
  all: [
    { heading: "Supplier", cell: (r) => r.supplier_name },
    { heading: "What", cell: (r) => r.what },
    { heading: "Order line", cell: (r) => r.order_line },
    { heading: "Raised", cell: (r) => short(r.raised_on), isDate: true },
    { heading: "Status", cell: (r) => r.status_label },
  ],
};

const itemKey = (item: PurchasingItem) =>
  item.kind === "purchase_order" ? item.purchase_order_no : item.id;

export function PurchasingPage() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  // A run of placements, finished: what it did ("Done: placed 3, skipped 1.").
  const placementNotice = (useLocation().state as { notice?: string } | null)
    ?.notice;
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const tab: PurchasingTab = isTab(rawTab) ? rawTab : "to_place";
  const q = params.get("q") || undefined;
  const page = Number(params.get("page")) || 1;
  const [result, setResult] = useState<PurchasingList | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A write's outcome ("Chased Ultima Displays.") or why it was refused.
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setResult(null);
    adapter
      .listPurchasing({ tab, q, page })
      .then((r) => !cancelled && setResult(r))
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Could not load purchasing.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, tab, q, page, reload]);

  const setParam = (key: "tab" | "q" | "page", value: string | null) => {
    setNotice(null);
    setFailure(null);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: key !== "tab" },
    );
  };

  /** Run a write, then re-read the tab: its counts and rows move with it. */
  const write = useCallback(
    async (key: string, run: () => Promise<void>, done: string) => {
      setBusyKey(key);
      setNotice(null);
      setFailure(null);
      try {
        await run();
        setNotice(done);
        setReload((n) => n + 1);
      } catch (err) {
        setFailure(orderStepFailure(err).summary);
      } finally {
        setBusyKey(null);
      }
    },
    [],
  );

  const rowHref = (row: PurchasingRow) =>
    paths.purchaseOrderDetail({
      purchaseOrderNo: row.purchase_order_no,
      tab: STEP_TAB[row.step],
    });
  const firstToPlace = result?.items.find(
    (item): item is PurchasingRow =>
      item.kind === "purchase_order" && !!item.placement_url,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl">Purchasing</h1>
          <p className="text-sm text-muted-foreground">
            Purchase orders with suppliers: placing them, and following them in.
            What they cost is under{" "}
            <Link to={paths.purchaseOrders()} className="underline">
              Finance › Purchase orders
            </Link>
            .
          </p>
        </div>
        {tab === "to_place" && firstToPlace && (
          <Button asChild variant="outline">
            <Link
              to={paths.placement({
                purchaseOrderNo: firstToPlace.purchase_order_no,
                queue: { done: 0, skipped: [] },
              })}
            >
              Place one at a time →
            </Link>
          </Button>
        )}
      </div>

      <div
        role="tablist"
        aria-label="Purchasing"
        className="flex flex-wrap gap-1 border-b border-border pb-3"
      >
        {TABS.map(({ tab: key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setParam("tab", key === "to_place" ? null : key)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              tab === key
                ? "bg-secondary text-secondary-foreground"
                : "text-muted-foreground hover:bg-muted",
            )}
          >
            {label}
            {result && (
              <span className="ml-1.5 tabular-nums opacity-70">
                {result.counts[key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {(notice ?? placementNotice) && (
        <p role="status" className="text-sm">
          {notice ?? placementNotice}
        </p>
      )}
      {failure && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{failure}</AlertDescription>
        </Alert>
      )}
      <Input
        type="search"
        placeholder="Search suppliers, orders, POs"
        aria-label="Search purchasing"
        defaultValue={q ?? ""}
        onKeyDown={(e) => {
          if (e.key === "Enter")
            setParam("q", e.currentTarget.value.trim() || null);
        }}
        className="w-64"
      />

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            Could not load purchasing: {error}
          </AlertDescription>
        </Alert>
      )}
      {!result && !error && (
        <div className="space-y-2" aria-label="Loading purchasing">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {result && result.items.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {q ? "Nothing matches that search." : EMPTY[tab]}
        </p>
      )}
      {result && result.items.length > 0 && (
        <>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="text-left">
                <th className="w-4 py-1.5 pr-2 font-normal">
                  <span className="sr-only">Late or at risk</span>
                </th>
                <th className="py-1.5 pr-3 font-normal">PO</th>
                {COLUMNS[tab].map((c) => (
                  <th key={c.heading} className="py-1.5 pr-3 font-normal">
                    {c.heading}
                  </th>
                ))}
                {tab === "awaiting_acknowledgement" && (
                  <th className="py-1.5 font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {result.items.map((item) =>
                item.kind === "rejected_line" ? (
                  <RejectedLineTableRow key={itemKey(item)} row={item} />
                ) : item.kind === "purchase_order" ? (
                  <tr
                    key={itemKey(item)}
                    onClick={() => navigate(rowHref(item))}
                    className="cursor-pointer border-t border-border align-top hover:bg-muted/50"
                  >
                    <td className="py-2 pr-2 pt-3.5">
                      <Dot row={item} />
                    </td>
                    <td className="py-2 pr-3 font-medium tabular-nums">
                      <Link
                        to={rowHref(item)}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[color:var(--color-primary)] hover:underline"
                      >
                        {item.purchase_order_no}
                      </Link>
                    </td>
                    {COLUMNS[tab].map((c) => (
                      <td
                        key={c.heading}
                        className={cn("py-2 pr-3", c.isDate && "tabular-nums")}
                      >
                        {c.cell(item)}
                      </td>
                    ))}
                    {tab === "awaiting_acknowledgement" && (
                      <td
                        className="py-1.5 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.chase_url ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyKey === item.purchase_order_no}
                            onClick={() =>
                              write(
                                item.purchase_order_no,
                                () => adapter.chasePurchaseOrder(item),
                                `Chased ${item.supplier_name}: a reminder went with a new link.`,
                              )
                            }
                          >
                            Chase
                          </Button>
                        ) : (
                          <Button asChild size="sm" variant="outline">
                            <Link to={rowHref(item)}>Open</Link>
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ) : null,
              )}
            </tbody>
          </table>
          {tab === "awaiting_acknowledgement" && (
            <p className="text-xs text-muted-foreground">
              A line rejected by the supplier leaves this list and goes back to{" "}
              <i>To place</i>, marked "Rejected by supplier".
            </p>
          )}
          {result.total_pages > 1 && (
            <ListPagination
              page={result.page}
              totalPages={result.total_pages}
              onPageChange={(p) => setParam("page", String(p))}
            />
          )}
        </>
      )}
    </div>
  );
}

/** A line its supplier rejected, on To place: *Change supplier…* is the order's Release step for the line. */
function RejectedLineTableRow({ row }: { row: RejectedLineRow }) {
  return (
    <tr className="border-t border-border align-top">
      <td className="py-2 pr-2 pt-3.5">
        <span
          className="inline-block size-2 rounded-full bg-red-600"
          role="img"
          aria-label={row.status_label}
        />
      </td>
      <td className="py-2 pr-3 font-medium tabular-nums">
        <Link
          to={paths.purchaseOrderDetail({
            purchaseOrderNo: row.purchase_order_no,
            tab: "lines",
          })}
          className="text-[color:var(--color-primary)] hover:underline"
        >
          {row.line_reference}
        </Link>
      </td>
      <td className="py-2 pr-3">{row.supplier_name}</td>
      <td className="py-2 pr-3">
        {row.what}
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs">
          <Badge variant="error">{row.status_label}</Badge>
          {row.reason && (
            <span className="text-muted-foreground">{row.reason}</span>
          )}
        </span>
      </td>
      <td className="py-2 pr-3">{row.order_line}</td>
      <td className="py-2 pr-3 tabular-nums">{short(row.rejected_on)}</td>
      <td className="py-2 pr-3">
        <span className="tabular-nums">{short(row.needed_by)}</span>
        <Button asChild size="sm" variant="outline" className="ml-2">
          <Link
            to={paths.orderDetail({
              orderNo: row.order_no,
              step: "release",
              line: row.order_line_no,
            })}
          >
            Change supplier…
          </Link>
        </Button>
      </td>
    </tr>
  );
}
